import '../core/config/loadEnv.js'
import { isString, isNonEmptyString } from '../common/utils/type-guards.js'


import OpenAI from 'openai'
import { calculateDistance } from '@my-roadtrip/utils'
import { createLogger } from '../common/utils/logger.js'
import { AIModelSelector } from './aiModelSelector.service.js'
import { costTracker } from './cost-tracker.service.js'
import type { AIModelTechnicalName, AIModelTier } from '@my-roadtrip/types'

const logger = createLogger('OpenAIService')

const DEFAULT_POI_MAX_TOKENS = 3000

function getEnvNumber(name: string, fallback: number): number {
  const raw = process.env[name]
  if (!raw) return fallback
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

// Validate API key at startup
if (!process.env.OPENAI_API_KEY) {
  logger.warn('OPENAI_API_KEY not configured - OpenAI features will be disabled')
}

const openai = new OpenAI({
  apiKey: process.env.OPENAI_API_KEY || 'dummy-key-for-disabled-mode'
})

interface OpenAIPOIResponse {
  name?: string
  description?: string
  location?: { lat?: number; lng?: number }
  category?: string
  tags?: string[]
  uniqueness?: number
  rating?: number
  confidence?: number
  relevance?: number
}

interface GenerateItineraryPayload {
  origin: string
  destinations: string[]
  days: number
  travelers: string
  travelMode: string
  pace: string
  themes: string[]
  language?: string
  preferences?: string
  budgetLevel?: string
}

interface GenerateItineraryResponse {
  planName?: string
  description?: string
  tags?: string[]
  stages?: Array<{
    name?: string
    type?: string
    day?: number
    summary?: string
    location?: {
      lat?: number | string
      lng?: number | string
      address?: string
    }
    durationMinutes?: number | string
    arrivalTime?: string
    departureTime?: string
  }>
  route?: {
    distanceKm?: number | string
    durationMinutes?: number | string
  }
  settings?: Record<string, unknown>
  metadata?: {
    model?: string
  }
}

interface OpenAITrackingContext {
  userId?: string
  feature?: string
}

export class OpenAIService {
  private async trackUsage(model: string, usage: { prompt_tokens?: number; completion_tokens?: number } | null | undefined, tracking?: OpenAITrackingContext) {
    const inputTokens = usage?.prompt_tokens ?? 0
    const outputTokens = usage?.completion_tokens ?? 0
    if (!inputTokens && !outputTokens) {
      return
    }

    try {
      await costTracker.trackOpenAI({
        model,
        inputTokens,
        outputTokens,
        userId: tracking?.userId,
        feature: tracking?.feature
      })
    } catch (error) {
      logger.warn('Failed to track OpenAI usage', { model, error })
    }
  }

  /**
   * Suggest POIs using OpenAI with enhanced prompting
   */
  async suggestPOIs(
    location: { lat: number; lng: number },
    categories: string[],
    radiusKm: number,
    preferences?: {
      prompt?: string
      preferScenic?: boolean
      avoidHighways?: boolean
      language?: string
      // Enhanced context
      fromLocation?: { name: string; lat: number; lng: number }
      toLocation?: { name: string; lat: number; lng: number }
      routeDistanceKm?: number
      departureTime?: string
      arrivalTime?: string
      planName?: string
      existingStages?: Array<{ name: string; type: string }>
    },
    userTier: AIModelTier = 'standard',
    preferredModel?: AIModelTechnicalName,
    tracking?: OpenAITrackingContext
  ): Promise<Array<{
    name: string
    description: string
    location: { coordinates: { lat: number; lng: number } }
    category: string
    tags: string[]
    uniqueness: number
    rating?: number
    confidence?: number
    relevance?: number
  }>> {
    const categoryStr = categories.length > 0 ? categories.join(', ') : 'interesting places'
    const language = preferences?.language || 'fr'
    const safeRadiusKm = Math.max(1, radiusKm)

    // Calculate distance constraints (more generous preferred zone)
    const preferredMaxKm = Math.max(2, Math.round(safeRadiusKm * 0.85))
    const absoluteMaxKm = safeRadiusKm

    // Build ultra-contextual prompt with ALL available information
    const contextParts: string[] = []

    // Determine search mode: ROUTE (corridor) vs STAGE (circular)
    const isRouteSearch = !!(preferences?.fromLocation && preferences?.toLocation)

    if (isRouteSearch) {
      const routeDist = preferences.routeDistanceKm ?? 0
      const from = preferences.fromLocation!
      const to = preferences.toLocation!
      contextParts.push(`📍 ROUTE CONTEXT (CORRIDOR SEARCH MODE):
- Departure: "${from.name}" (${from.lat}, ${from.lng})
- Destination: "${to.name}" (${to.lat}, ${to.lng})
- Route distance: ${routeDist.toFixed(1)}km
- Search point: MID-ROUTE between the two stages (${location.lat}, ${location.lng})

🎯 CRITICAL: Find places ALONG THE ROUTE, not just near the midpoint!
- POIs must be ACCESSIBLE from the route (< ${Math.min(10, safeRadiusKm)}km perpendicular detour)
- Prioritize places BETWEEN departure and destination
- Think "worthy stops along the journey", not "things near the center"
- Example: If route goes Paris → Lyon, suggest places IN THE DIRECTION of travel (Fontainebleau, Auxerre, etc.)
- Do NOT suggest places far perpendicular to the route axis`)
    } else {
      contextParts.push(`📍 STAGE SEARCH MODE (CIRCULAR):
- Center point: ${location.lat}, ${location.lng}
- Search radius: ${safeRadiusKm}km
- Find places AROUND this specific location in all directions`)
    }

    if (preferences?.departureTime || preferences?.arrivalTime) {
      contextParts.push(`⏰ TIMING CONTEXT:
${preferences.departureTime ? `- Departure time: ${preferences.departureTime}` : ''}
${preferences.arrivalTime ? `- Arrival time: ${preferences.arrivalTime}` : ''}
- Suggest places appropriate for this time window`)
    }

    if (preferences?.planName) {
      contextParts.push(`🗺️ TRIP CONTEXT:
- Trip name: "${preferences.planName}"
- This POI will be inserted as a stop in an existing road trip`)
    }

    if (preferences?.existingStages && preferences.existingStages.length > 0) {
      const stageTypes = preferences.existingStages.map(s => s.type).filter(Boolean)
      const stageNames = preferences.existingStages.slice(0, 5).map(s => s.name).join(', ')
      contextParts.push(`🎯 EXISTING STOPS:
- Already planned: ${stageNames}${preferences.existingStages.length > 5 ? '...' : ''}
- Types seen: ${[...new Set(stageTypes)].join(', ')}
- Avoid suggesting similar/duplicate places`)
    }

    const enrichedContext = contextParts.length > 0
      ? '\n\n' + contextParts.join('\n\n')
      : ''

    const prompt = `
You are a meticulous local travel expert AND verifier specialized in road trip planning. Find 15-20 VERIFIED, REAL places for categories [${categoryStr}] near coordinates ${location.lat}, ${location.lng}.${enrichedContext}

🎯 CATEGORY INTERPRETATION (FLEXIBLE):
- Primary categories requested: [${categoryStr}]
- Also include RELATED categories for variety (e.g., viewpoint → also include scenic spots, panoramas, overlooks)
- If user asks for "viewpoint", include: belvederes, observation points, scenic lookouts, panoramic views
- If user asks for "restaurant", include: bistros, brasseries, cafés with food
- If user asks for "museum", include: galleries, cultural centers, historic sites
- Mix famous places with local hidden gems

🚨 CRITICAL DISTANCE RULES (STRICTLY ENFORCED):
- ABSOLUTE MAXIMUM DISTANCE: ${absoluteMaxKm}km from center point ${location.lat}, ${location.lng}
- PREFERRED ZONE: 0-${preferredMaxKm}km (prioritize this zone heavily)
- ❌ REJECT any POI beyond ${absoluteMaxKm}km - DO NOT include it even if interesting
- Calculate distance accurately: Use haversine formula or precise geodesic distance
- Double-check coordinates before including

🔍 VERIFICATION REQUIREMENTS (BALANCED APPROACH):
- Include places you are 90%+ certain exist today (minimum threshold 50% but prioritize 90%+ confidence)
- Use real coordinates from actual map data (Google Maps, OpenStreetMap, official sources)
- Cross-reference place names with known databases when possible
- Provide confidence score (0-100) based on verification certainty:
  * 90-100: Place exists, coordinates verified from multiple sources
  * 70-89: Place likely exists, coordinates from one reliable source
  * 50-69: Place probably exists but coordinates uncertain
  * <50: DO NOT INCLUDE (minimum threshold)

❌ MANDATORY REJECTIONS (STRICT ONLY FOR OBVIOUS FAKE):
- Completely invented places with no basis in reality
- Coordinates that don't match geographic region
- Duplicate entries of the same place

✅ ACCEPTABLE PLACES (INCLUDE THESE - GENEROUS):
- Official viewpoints: "Belvédère de la Roque-Gageac", "Point de vue de [X]"
- Local landmarks: churches, monuments, squares, parks
- Established businesses: restaurants, châteaux, museums
- Natural sites: gorges, waterfalls, lakes, forests
- Cultural sites: Roman ruins, abbeys, historic centers
- Scenic spots: panoramic views, river overlooks
- Tourist attractions with local significance

📍 COORDINATE ACCURACY (CRITICAL - ZERO TOLERANCE):
- Provide ABSOLUTE GPS coordinates (5+ decimal places: 48.85660, 2.35220)
- ⚠️ NEVER return relative offsets or deltas from the search center
- ⚠️ NEVER return coordinates near (0, 0) - this indicates an error
- ⚠️ Coordinates MUST be in the same region as search center ${location.lat}, ${location.lng}
- Example: If search is in France (lat ~43-49, lng ~-5 to 8), POI coords must be in that range
- Never use placeholder coordinates (0,0 or center point)
- Never approximate or guess coordinates
- Verify coordinates match the actual place location
- Cross-check coordinates against Google Maps or OpenStreetMap data
- Test: Does the returned coordinate make geographic sense? (e.g., France POI should have French coords)

📊 SORTING & SCORING:
1. First: Sort by relevance (highest first)
2. Then: Sort by confidence (most verified first)
3. Finally: Sort by distance (nearest first)
- Relevance (0-100): Match to categories + user preference + distance proximity
- Confidence (0-100): Verification certainty as defined above

📝 CONTENT QUALITY:
- Name: Exact official name (not generic descriptions)
- Description: 1-2 factual sentences (<= 220 chars)
- Tags: 2-5 specific, relevant tags
- Category: Must match one of: ${categories.join(', ') || 'poi'}
- Rating: Real rating if available (0-5 scale)
- Uniqueness: How special/unique (0-100)

🌐 Language: ${language}

👤 User preferences: ${preferences?.prompt ?? 'none'}
${preferences?.preferScenic ? '🏞️ Prioritize scenic viewpoints and natural beauty\n' : ''}${preferences?.avoidHighways ? '🚫 Avoid highway-adjacent or roadside stops\n' : ''}

⚠️ QUANTITY AND DIVERSITY:
- Aim for 10-15 places to give users good variety
- Mix different types: viewpoints, monuments, restaurants, nature spots
- Include both famous landmarks AND hidden gems
- Better 12 interesting places than 3 ultra-verified ones
- Prioritize DIVERSITY over strict verification

Return ONLY raw JSON array (no markdown, no code blocks, no explanations):
[
  {
    "name": "Exact Place Name",
    "description": "Brief factual description",
    "location": {"lat": 48.85660, "lng": 2.35220},
    "category": "${categories[0] || 'poi'}",
    "tags": ["specific", "relevant", "tags"],
    "uniqueness": 85,
    "rating": 4.5,
    "confidence": 95,
    "relevance": 92
  }
]`.trim()

    // Select appropriate model based on user tier
    const selectedModel = AIModelSelector.selectModel(userTier, preferredModel)
    const modelDisplayName = AIModelSelector.getDisplayName(selectedModel)

    logger.info('Generating POI suggestions', {
      model: selectedModel,
      modelDisplayName,
      userTier,
      location,
      categories,
      radiusKm
    })

    try {
      const response = await openai.chat.completions.create({
        model: selectedModel,
        messages: [
          {
            role: 'system',
            content:
              'You are a travel expert specializing in diverse, interesting points of interest for road trips. RULES: (1) Return 15-20 places for maximum variety. (2) Mix famous landmarks, local spots, viewpoints, restaurants, natural sites. (3) Use real GPS coordinates from the region. (4) Include both popular AND hidden gems. (5) Prioritize diversity over strict verification. Always return JSON with interesting places near the coordinates.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.45,
        max_tokens: getEnvNumber('OPENAI_MAX_TOKENS', DEFAULT_POI_MAX_TOKENS),
        presence_penalty: 0.1
      })

      const completionModel = response.model ?? selectedModel
      await this.trackUsage(completionModel, response.usage, tracking)

      const content = response.choices[0].message.content || '[]'

      // Clean markdown code blocks if present
      const cleanContent = content
        .replaceAll(/```json\n?/g, '')
        .replaceAll(/```\n?/g, '')
        .trim()

      let parsed: unknown
      try {
        parsed = JSON.parse(cleanContent)
      } catch (parseError) {
        logger.error('Failed to parse OpenAI POI response', { content: cleanContent, error: parseError })
        throw new Error('Failed to parse OpenAI response - invalid JSON format')
      }

      if (!Array.isArray(parsed)) {
        throw new TypeError('Invalid response format: expected an array of POIs')
      }

      // SECURITY FIX (ISSUE-006): Validate array size before processing
      if (parsed.length === 0 || parsed.length > 20) {
        throw new Error(`Expected 1-20 POIs, got ${parsed.length}`)
      }

      const pois = (parsed as OpenAIPOIResponse[])
        .map((poi, idx) => {
          // Validate name field
          if (!poi.name || typeof poi.name !== 'string' || poi.name.length === 0 || poi.name.length > 200) {
            throw new Error(`POI ${idx}: Invalid name (must be 1-200 chars)`)
          }

          const rawUniqueness = typeof poi.uniqueness === 'number'
            ? poi.uniqueness
            : Number.parseFloat(String(poi.uniqueness ?? ''))
          const uniqueness = Number.isFinite(rawUniqueness)
            ? Math.min(Math.max(rawUniqueness, 0), 100)
            : 50

          const rawRating = typeof poi.rating === 'number'
            ? poi.rating
            : Number.parseFloat(String(poi.rating ?? ''))
          const rating = Number.isFinite(rawRating) ? rawRating : undefined

          const rawConfidence = typeof poi.confidence === 'number'
            ? poi.confidence
            : Number.parseFloat(String(poi.confidence ?? ''))
          const confidence = Number.isFinite(rawConfidence)
            ? Math.min(Math.max(rawConfidence, 0), 100)
            : undefined

          const rawRelevance = typeof poi.relevance === 'number'
            ? poi.relevance
            : Number.parseFloat(String(poi.relevance ?? ''))
          const relevance = Number.isFinite(rawRelevance)
            ? Math.min(Math.max(rawRelevance, 0), 100)
            : undefined

          const lat = Number.parseFloat(String(poi.location?.lat ?? ''))
          const lng = Number.parseFloat(String(poi.location?.lng ?? ''))

          // CRITICAL FIX: Reject invalid coordinates (don't fallback)
          if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
            throw new TypeError(`POI ${idx} "${poi.name}": Invalid coordinates (lat=${lat}, lng=${lng})`)
          }

          // CRITICAL FIX (ISSUE-GULF-OF-GUINEA): Reject coordinates near 0,0 or too far from search center
          // OpenAI sometimes returns relative offsets instead of absolute GPS coordinates
          const isNearZeroZero = Math.abs(lat) < 0.1 && Math.abs(lng) < 0.1

          // Calculate actual distance in km (not degrees!)
          const distanceKm = calculateDistance(location, { lat, lng }) / 1000
          const maxReasonableDistanceKm = safeRadiusKm * 2.5 // Allow 2.5x radius for flexibility
          const isTooFarFromCenter = distanceKm > maxReasonableDistanceKm

          if (isNearZeroZero || isTooFarFromCenter) {
            logger.warn('OpenAI returned invalid coordinates - rejecting POI', {
              name: poi.name,
              returnedCoords: { lat, lng },
              searchCenter: location,
              isNearZeroZero,
              isTooFarFromCenter,
              distanceKm: distanceKm.toFixed(1),
              maxDistanceKm: maxReasonableDistanceKm.toFixed(1),
              radiusKm: safeRadiusKm
            })
            throw new TypeError(`POI ${idx} "${poi.name}": Coordinates too far from search area or near 0,0 (distance=${distanceKm.toFixed(1)}km, max=${maxReasonableDistanceKm.toFixed(1)}km)`)
          }

          // Validate category if present
          const allowedCategories = categories.length > 0 ? categories : ['poi']
          const category = poi.category && isString(poi.category)? poi.category
            : allowedCategories[0] || 'poi'

          return {
            name: poi.name.trim(),
            description: typeof poi.description === 'string' ? poi.description.slice(0, 500) : '',
            location: {
              coordinates: {
                lat,
                lng
              }
            },
            category,
            tags: Array.isArray(poi.tags) ? poi.tags.filter(t => typeof t === 'string').slice(0, 10) : [],
            uniqueness,
            rating,
            confidence,
            relevance
          }
        })
        .filter(poi => poi.uniqueness >= 30) // Lowered from 50 to 30 for more variety

      if (!pois.length) {
        throw new Error('No POIs returned by OpenAI')
      }

      return pois
    } catch (error) {
      logger.error('Failed to generate POI suggestions', { error })
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to generate suggestions')
    }
  }

  /**
   * Enhance plan description with AI
   */
  async enhancePlanDescription(
    plan: { name: string; stages: Array<{ name: string } & Record<string, unknown>> },
    tracking?: OpenAITrackingContext
  ): Promise<string> {
    if (!plan.name?.trim()) {
      throw new Error('Plan name is required for enhancement')
    }

    if (!plan.stages || plan.stages.length === 0) {
      throw new Error('Plan must include at least one stage')
    }

    const prompt = `Write a compelling 2-paragraph description for this road trip:
Name: ${plan.name}
Stages: ${plan.stages.map(s => s.name).join(' → ')}

Make it engaging and highlight key attractions. Max 200 words.`

    try {
      const response = await openai.chat.completions.create({
        model: process.env.OPENAI_MODEL || 'gpt-4o-mini',
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.8,
        max_tokens: 300
      })

      await this.trackUsage(
        response.model ?? process.env.OPENAI_MODEL ?? 'gpt-4o-mini',
        response.usage,
        tracking
      )

      return response.choices[0].message.content || ''
    } catch (error) {
      logger.error('Failed to enhance plan description', { error })
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to enhance description')
    }
  }

  /**
   * Generate an itinerary for a road trip
   */
  async generateItinerary(
    payload: GenerateItineraryPayload,
    tracking?: OpenAITrackingContext
  ): Promise<GenerateItineraryResponse> {
    const language = payload.language ?? 'fr'
    const prompt = `Tu es un expert en road trips. Crée un itinéraire pour ${payload.days} jour(s) en partant de ${payload.origin} et en visitant ${payload.destinations.join(', ')}.

Contraintes:
- Mode de transport principal: ${payload.travelMode}
- Rythme attendu: ${payload.pace}
- Profil de voyageurs: ${payload.travelers}
- Thématiques prioritaires: ${payload.themes.join(', ')}
- Budget: ${payload.budgetLevel ?? 'standard'}
${payload.preferences ? `- Préférences utilisateur: ${payload.preferences}` : ''}

IMPORTANT: Réponds UNIQUEMENT en JSON valide sans Markdown.

Format attendu:
{
  "planName": "string",
  "description": "string (max 400 caractères)",
  "tags": ["string"],
  "route": { "distanceKm": number, "durationMinutes": number },
  "stages": [
    {
      "name": "string",
      "type": "start|waypoint|overnight|end|poi",
      "day": number,
      "summary": "string",
      "location": { "lat": number, "lng": number, "address": "string" },
      "durationMinutes": number,
      "arrivalTime": "ISO-8601",
      "departureTime": "ISO-8601"
    }
  ]
}

Règles clés:
- Inclure 5 à 12 étapes maximum
- Toujours fournir des coordonnées lat/lng réalistes pour chaque étape
- La première étape est le départ, la dernière l'arrivée
- Réponse en langue: ${language}`

    const completion = await openai.chat.completions.create({
      model: process.env.OPENAI_MODEL || 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'Tu es un planificateur de voyages qui retourne toujours du JSON strictement valide.'
        },
        { role: 'user', content: prompt }
      ],
      temperature: 0.7,
      max_tokens: 2000
    })

    await this.trackUsage(
      completion.model ?? process.env.OPENAI_MODEL ?? 'gpt-4o-mini',
      completion.usage,
      tracking
    )

    const content = completion.choices[0]?.message?.content ?? '{}'
    const cleaned = content
      .replaceAll(/```json\s*/gi, '')
      .replaceAll('```', '')
      .trim()

    let parsed: GenerateItineraryResponse
    try {
      parsed = JSON.parse(cleaned) as GenerateItineraryResponse
    } catch (parseError) {
      logger.error('Failed to parse OpenAI itinerary response', { content: cleaned, error: parseError })
      throw new Error('Failed to parse OpenAI response - invalid JSON format')
    }

    const resolvedModel = completion.model ?? process.env.OPENAI_MODEL ?? 'gpt-4o-mini'
    parsed.metadata = parsed.metadata
      ? { ...parsed.metadata, model: resolvedModel }
      : { model: resolvedModel }
    return parsed
  }

  async generateBadgeConcept(options: {
    prompt?: string
    preset?: string
    tone?: string
    audience?: string
    rarity?: 'common' | 'rare' | 'epic' | 'legendary'
    locale?: string
    style?: string
  }, tracking?: OpenAITrackingContext): Promise<{
    name: string
    description: string
    requirement: string
    icon: string
    accentColor: string
    backgroundGradient: string
    badgeCategory: string
    tags: string[]
    rarity: 'common' | 'rare' | 'epic' | 'legendary'
    iconPrompt: string
    aiPrompt: string
  }> {
    const locale = options.locale || 'fr'
    const rarity = options.rarity || 'rare'
    const preset = options.preset || 'Explorer'
    const tone = options.tone || 'inspiring'
    const audience = options.audience || 'road trip community'
    const additionalPrompt = options.prompt?.trim() || ''
    const style = options.style || 'modern minimal vector icon'

    const prompt = `Tu es un directeur artistique pour une application de road-trip.
Tu dois créer un badge ${rarity} pour la communauté (${audience}).
Preset concept: ${preset}.
Style iconographique: ${style}.
Réponds UNIQUEMENT avec un JSON strict (pas de markdown) respectant ce format:
{
  "name": "Nom court du badge (max 4 mots)",
  "description": "Description motivationnelle (max 120 caractères)",
  "requirement": "Condition explicite pour débloquer le badge",
  "icon": "Emoji unique ou combinaison d'emoji (1-3 caractères)",
  "accentColor": "Couleur principale HEX (#AABBCC)",
  "backgroundGradient": "Deux couleurs HEX séparées par une virgule (#123456,#654321)",
  "badgeCategory": "Catégorie thématique (ex: Exploration, Communauté, Performance...)",
  "tags": ["mot1", "mot2"],
  "iconPrompt": "Prompt anglais concis pour générer un pictogramme cohérent",
  "aiPrompt": "Prompt complet expliquant le concept pour un modèle d'images"
}
Langue de sortie: ${locale}.
Ton: ${tone}.
Contexte additionnel: ${additionalPrompt || '—'}`

    const response = await openai.chat.completions.create({
      model: process.env.OPENAI_MODEL || 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 800,
      messages: [
        {
          role: 'system',
          content: 'Tu es un directeur artistique spécialisé dans les badges gamifiés. Tu dois toujours répondre en JSON valide, sans markdown.'
        },
        { role: 'user', content: prompt }
      ]
    })

    await this.trackUsage(
      response.model ?? process.env.OPENAI_MODEL ?? 'gpt-4o-mini',
      response.usage,
      tracking
    )

    const content = response.choices[0].message.content || '{}'
    const clean = content.replaceAll(/```json|```/g, '').trim()

    let parsed: any
    try {
      parsed = JSON.parse(clean)
    } catch (parseError) {
      logger.error('Failed to parse OpenAI badge concept response', { content: clean, error: parseError })
      throw new Error('Failed to parse OpenAI response - invalid JSON format')
    }

    return {
      name: parsed.name || 'Badge Explorer',
      description: parsed.description || 'Récompense les explorateurs inspirés',
      requirement: parsed.requirement || 'Compléter un nouveau road trip',
      icon: parsed.icon || '🗺️',
      accentColor: parsed.accentColor || '#FFB300',
      backgroundGradient: parsed.backgroundGradient || '#FFB300,#FF6F00',
      badgeCategory: parsed.badgeCategory || preset,
      tags: Array.isArray(parsed.tags) ? parsed.tags : [],
      rarity,
      iconPrompt: parsed.iconPrompt || 'Modern flat vector icon of adventure badge, minimal, high contrast',
      aiPrompt: parsed.aiPrompt || prompt
    }
  }

  async generateBadgeIconImage(options: {
    prompt: string
    variant?: 'flat' | '3d' | 'outlined'
    size?: '256x256' | '512x512'
  }): Promise<string> {
    const size = options.size || '256x256'

    // Extract style instruction from variant
    let styleInstruction: string
    switch (options.variant) {
      case '3d':
        styleInstruction = '3D render, cinematic lighting'
        break
      case 'outlined':
        styleInstruction = 'Outlined icon, bold stroke'
        break
      default:
        styleInstruction = 'Flat vector icon'
    }

    const composedPrompt = `${styleInstruction}, ${options.prompt}, centered icon, transparent background`

    const result = await openai.images.generate({
      model: process.env.OPENAI_IMAGE_MODEL || 'gpt-image-1',
      prompt: composedPrompt,
      size,
      response_format: 'b64_json'
    })

    const image = result.data?.[0]?.b64_json
    if (!image) {
      throw new Error('Failed to generate badge icon image')
    }

    // Track cost (DALL-E 3 standard pricing approx $0.04 per image)
    // Adjust based on actual model used
    const cost = process.env.OPENAI_IMAGE_MODEL?.includes('dall-e-2') ? 200 : 400 // cents * 100

    await costTracker.track({
      provider: 'openai',
      service: 'image-generation',
      cost,
      usage: 1,
      unit: 'image',
      feature: 'badge-generation'
    })

    return image
  }

  /**
   * Parse user text input into activity segments for route enrichment
   * Extracts: activities, locations, times, categories, and confidence scores
   */
  async parseActivitySegments(
    input: {
      text: string
      type: 'text' | 'voice'
      confidence: number
      language: string
    },
    tracking?: OpenAITrackingContext
  ): Promise<Array<{
    id: string
    type: 'departure' | 'stop' | 'activity' | 'arrival'
    description: string
    searchQuery?: string
    categories: string[]
    tags: string[]
    duration?: number
    relativeTime?: { value: number; unit: 'minutes' | 'hours' | 'days' }
    absoluteTime?: Date
    estimatedArrival?: Date
    estimatedDeparture?: Date
    confidence: number
  }>> {
    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()
    const dayOfWeek = now.toLocaleDateString(input.language, { weekday: 'long' })
    
    const prompt = `Tu es un expert en analyse de voyages routiers avec compréhension contextuelle avancée. Analyse ce texte utilisateur et extrait les étapes/activités avec TOUTES les informations temporelles et spatiales.

📅 CONTEXTE TEMPOREL ACTUEL:
- Date/heure actuelle: ${now.toISOString()}
- Heure locale: ${currentHour}:${currentMinute.toString().padStart(2, '0')}
- Jour: ${dayOfWeek}

📍 Texte utilisateur: "${input.text}"

🎯 ANALYSE INTELLIGENTE REQUISE:

Pour CHAQUE étape/activité mentionnée, extrais:

1. **type**: 
   - "departure" (départ) - quand l'utilisateur part d'un lieu
   - "stop" (arrêt) - lieu précis avec adresse
   - "activity" (activité) - activité sans lieu fixe (balade, vue, pause)
   - "arrival" (arrivée) - destination finale

2. **description**: Description originale EXACTE du texte utilisateur

3. **searchQuery**: 
   - Pour lieu précis: nom exact ("Intermarché", "rue du Chaine", "Château de Chambord")
   - Pour activité: type d'activité ("point de vue", "restaurant italien", "balade nature")
   - INTELLIGENT: Si "Intermarché" → chercher dans rayon proche, pas une adresse fixe

4. **categories**: Catégories applicables (PLUSIEURS possibles):
   - restaurant, hotel, grocery, nature, viewpoint, culture, health, fuel, parking, beach, mountain, shopping, activity, entertainment

5. **tags**: Tags contextuels pertinents:
   - sunset, sunrise, golden-hour, night, outdoor, indoor, wifi, parking, accessible, family, romantic, scenic, historic

6. **duration**: Durée estimée RÉALISTE en minutes:
   - Courses: 20-45 min
   - Restaurant: 60-90 min
   - Visite château: 90-180 min
   - Balade nature: 30-120 min
   - Docteur: 30-60 min
   - Carburant: 10-15 min

7. **relativeTime**: Temps RELATIF par rapport à maintenant (${currentHour}:${currentMinute}):
   - "dans 15 min" → {value: 15, unit: "minutes"}
   - "dans 2h" → {value: 2, unit: "hours"}
   - "demain matin" → {value: 1, unit: "days"} + absoluteTime calculé
   - "ce soir" → calculer heures restantes jusqu'à 19h-20h

8. **absoluteTime**: Temps ABSOLU calculé intelligemment:
   - "18h30" → ISO avec date du jour: "${now.toISOString().split('T')[0]}T18:30:00Z"
   - "demain 9h" → ISO avec date + 1 jour
   - "ce soir" → ISO avec 19h00 du jour
   - "avant coucher de soleil" → ISO avec 18h00-19h30 (selon saison)

9. **weatherSensitive**: boolean - true si météo importante:
   - Balade, vue, plage, activités outdoor → true
   - Restaurant, courses, intérieur → false

10. **timeOfDayPreference**: "morning" | "afternoon" | "evening" | "night" | "sunset" | "sunrise" | null
    - "coucher de soleil" → "sunset"
    - "petit-déjeuner" → "morning"
    - "dîner" → "evening"

11. **fixedLocation**: boolean
    - Adresse précise (rue, numéro) → true
    - Nom générique (Intermarché, restaurant) → false

12. **confidence**: Confiance (0-100) que cette extraction est correcte

🧠 EXEMPLES INTELLIGENTS:

Input: "Je sors du travail dans 15 min"
Output: {
  "type": "departure",
  "description": "Je sors du travail dans 15 min",
  "searchQuery": "lieu de travail",
  "categories": ["other"],
  "duration": 5,
  "relativeTime": {"value": 15, "unit": "minutes"},
  "absoluteTime": "${new Date(now.getTime() + 15 * 60000).toISOString()}",
  "fixedLocation": false,
  "confidence": 90
}

Input: "courses à Intermarché"
Output: {
  "type": "stop",
  "description": "courses à Intermarché",
  "searchQuery": "Intermarché",
  "categories": ["grocery", "shopping"],
  "duration": 30,
  "fixedLocation": false,
  "confidence": 95
}

Input: "docteur rue du Chaine"
Output: {
  "type": "stop",
  "description": "docteur rue du Chaine",
  "searchQuery": "rue du Chaine",
  "categories": ["health"],
  "duration": 45,
  "fixedLocation": true,
  "confidence": 80
}

Input: "balade avant coucher de soleil"
Output: {
  "type": "activity",
  "description": "balade avant coucher de soleil",
  "searchQuery": "point de vue panoramique",
  "categories": ["nature", "viewpoint"],
  "tags": ["sunset", "outdoor", "scenic", "golden-hour"],
  "duration": 60,
  "timeOfDayPreference": "sunset",
  "absoluteTime": "${new Date(now.setHours(18, 30)).toISOString()}",
  "weatherSensitive": true,
  "fixedLocation": false,
  "confidence": 85
}

Input: "restaurant vers 20h"
Output: {
  "type": "stop",
  "description": "restaurant vers 20h",
  "searchQuery": "restaurant",
  "categories": ["restaurant"],
  "duration": 90,
  "timeOfDayPreference": "evening",
  "absoluteTime": "${new Date(now.setHours(20, 0)).toISOString()}",
  "fixedLocation": false,
  "confidence": 90
}

⚠️ RÈGLES CRITIQUES:
- Calcule absoluteTime pour TOUTES les mentions temporelles (même implicites)
- Si "ce soir" → calcule 19h-20h du jour actuel
- Si "demain" → ajoute 1 jour à la date actuelle
- Si durée mentionnée ("30 minutes", "2h") → utilise-la exactement
- Si pas de durée → estime intelligemment selon le type d'activité
- TOUJOURS mettre fixedLocation: true pour adresses précises (rues, numéros)
- TOUJOURS mettre fixedLocation: false pour noms génériques (Intermarché, restaurant, point de vue)

Réponds UNIQUEMENT en JSON valide (PAS de markdown, PAS de \`\`\`json):
[
  {
    "type": "departure" | "stop" | "activity" | "arrival",
    "description": "texte original",
    "searchQuery": "nom du lieu ou activité",
    "categories": ["category1", "category2"],
    "tags": ["tag1", "tag2"],
    "duration": 30,
    "relativeTime": {"value": 15, "unit": "minutes"},
    "absoluteTime": "2024-12-06T18:30:00Z",
    "weatherSensitive": false,
    "timeOfDayPreference": null,
    "fixedLocation": false,
    "confidence": 85
  }
]`

    const selectedModel = 'gpt-4o-mini'

    try {
      const response = await openai.chat.completions.create({
        model: selectedModel,
        temperature: 0.3, // Low temperature for consistency
        max_tokens: 2000,
        messages: [
          {
            role: 'system',
            content: 'Tu es un assistant spécialisé dans l\'analyse de textes de voyages. Tu extrais les informations structurées en JSON valide, sans markdown ni code blocks.'
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      })

      await this.trackUsage(selectedModel, response.usage, tracking)

      const content = response.choices[0]?.message?.content || '[]'
      const cleaned = content
        .replaceAll(/```json\s*/gi, '')
        .replaceAll('```', '')
        .trim()

      let segments: any[] = []
      try {
        segments = JSON.parse(cleaned)
      } catch (parseError) {
        logger.error('Failed to parse OpenAI enrichment response', { content: cleaned, error: parseError })
        throw new Error('Failed to parse enrichment response - invalid JSON format')
      }

      // Enrich segments with sequence timing
      const now = new Date()
      let currentTime = new Date(now)

      const enrichedSegments = segments.map((seg: any, idx: number) => {
        const id = `seg_${idx}`

        // Calculate estimated arrival time
        let estimatedArrival: Date | undefined
        if (seg.absoluteTime) {
          estimatedArrival = new Date(seg.absoluteTime)
        } else if (seg.relativeTime) {
          estimatedArrival = new Date(currentTime)
          if (seg.relativeTime.unit === 'minutes') {
            estimatedArrival.setMinutes(estimatedArrival.getMinutes() + seg.relativeTime.value)
          } else if (seg.relativeTime.unit === 'hours') {
            estimatedArrival.setHours(estimatedArrival.getHours() + seg.relativeTime.value)
          } else if (seg.relativeTime.unit === 'days') {
            estimatedArrival.setDate(estimatedArrival.getDate() + seg.relativeTime.value)
          }
        }

        // Calculate estimated departure time
        let estimatedDeparture: Date | undefined
        if (estimatedArrival) {
          estimatedDeparture = new Date(estimatedArrival)
          estimatedDeparture.setMinutes(estimatedDeparture.getMinutes() + (seg.duration || 30))
          currentTime = new Date(estimatedDeparture)
        }

        return {
          id,
          type: seg.type || 'stop',
          description: seg.description || '',
          searchQuery: seg.searchQuery,
          categories: seg.categories || [],
          tags: seg.tags || [],
          duration: seg.duration || 30,
          relativeTime: seg.relativeTime,
          absoluteTime: seg.absoluteTime ? new Date(seg.absoluteTime) : undefined,
          estimatedArrival,
          estimatedDeparture,
          confidence: Math.min(100, Math.max(0, seg.confidence || 75))
        }
      })

      logger.info('Parsed enrichment segments', { count: enrichedSegments.length })
      return enrichedSegments
    } catch (error) {
      logger.error('Failed to parse enrichment segments', { error, textLength: input.text.length })
      throw error
    }
  }
}
